import React, { useState, useEffect } from 'react';
import { 
  Eye, 
  Search, 
  Filter,
  RefreshCw,
  Calendar,
  DollarSign,
  TrendingUp,
  TrendingDown,
  FileText,
  Clock,
  User,
  AlertCircle,
  Download,
  Activity,
  BarChart3,
  PieChart,
  Users,
  Target,
  Award,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ArrowUpRight,
  ArrowDownRight,
  MoreHorizontal,
  Filter as FilterIcon,
  CalendarDays,
  CreditCard,
  Wallet,
  CheckCircle,
  XCircle,
  ArrowLeft,
  Sparkles,
  Zap,
  Star,
  Crown
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import financialCaseApi from '../../services/accountant/financialCaseApi';
import Realtime from '../../services/realtime';
import { formatCurrency } from '../../utils/currencyUtils';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Cấu hình mặc định cho biểu đồ Line
const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: { position: 'top', labels: { usePointStyle: true } },
    tooltip: { enabled: true },
    title: { display: false }
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { autoSkip: true, maxTicksLimit: 10 }
    },
    y: {
      beginAtZero: true,
      grid: { color: 'rgba(0,0,0,0.05)' }
    }
  }
};

const AccountantStatistics = () => {
  const { token, user } = useAuth();
  const [financialCases, setFinancialCases] = useState([]);
  const [allCases, setAllCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [currencyFilter, setCurrencyFilter] = useState('all');
  const [selectedCase, setSelectedCase] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [statistics, setStatistics] = useState({
    total: 0,
    approved: 0,
    rejected: 0,
    totalAmount: 0,
    totalIncome: 0,
    totalExpense: 0,
    // VND stats
    totalIncomeVND: 0,
    totalExpenseVND: 0,
    netProfitVND: 0,
    pendingCasesVND: 0,
    cancelledCasesVND: 0,
    // USD stats
    totalIncomeUSD: 0,
    totalExpenseUSD: 0,
    netProfitUSD: 0,
    pendingCasesUSD: 0,
    cancelledCasesUSD: 0
  });
  
  // Chart và filter states
  const [selectedTimeRange, setSelectedTimeRange] = useState('today');
  const [selectedCurrency, setSelectedCurrency] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCharts, setShowCharts] = useState(true);
  const [chartData, setChartData] = useState(null);
  const [sellerStats, setSellerStats] = useState([]);
  
  // Seller detail popup states
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [showSellerDetailModal, setShowSellerDetailModal] = useState(false);
  const [sellerDetailData, setSellerDetailData] = useState(null);
  const [sellerPopupTimeRange, setSellerPopupTimeRange] = useState('all');
  const [sellerPopupCurrency, setSellerPopupCurrency] = useState('all');
  
  // Main list filter state
  const [selectedSellerForList, setSelectedSellerForList] = useState('all');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // Realtime: join accountant room and listen to created/updated events
  useEffect(() => {
    if (!user || !user.email) return;
    Realtime.connect();
    Realtime.joinAccountantRoom(user.email);

    const onCreated = (payload) => {
      if (!payload) return;
      toast.success(payload.message || 'Có financial case mới!');
      // Reload danh sách và thống kê
      loadFinancialCases();
    };

    const onUpdated = (payload) => {
      if (!payload) return;
      toast.success(payload.message || 'Financial case đã được cập nhật!');
      loadFinancialCases();
    };

    Realtime.on('financial-case-created', onCreated);
    Realtime.on('financial-case-updated', onUpdated);

    return () => {
      Realtime.off('financial-case-created', onCreated);
      Realtime.off('financial-case-updated', onUpdated);
    };
  }, [user]);

  // Load financial cases (chỉ approved và rejected, không có cancelled)
  const loadFinancialCases = async () => {
    try {
      setLoading(true);
      
      const response = await financialCaseApi.getAllFinancialCases(token);
      debugApiResponse(response);
      
      if (response.success) {
        // Lấy tất cả cases để tính toán statistics chính xác
        const allCasesData = response.data;
        setAllCases(allCasesData);

        // Chỉ lấy cases đã được xử lý (approved/rejected) cho hiển thị
        const processedCases = allCasesData.filter(case_ => 
          case_.status === 'approved' || case_.status === 'rejected'
        );
        
        setFinancialCases(processedCases);
        setFilteredCases(processedCases);
        
        // Tính statistics ban đầu (chỉ approved cases)
        const approvedCases = processedCases.filter(case_ => case_.status === 'approved');
        calculateStatistics(approvedCases);
        
        // Tính đếm pending/cancelled (pending = tất cả, cancelled/rejected = đã lọc)
        calculatePendingCancelledStats(allCasesData, allCasesData);
        generateChartData(processedCases);
        calculateSellerStatistics(processedCases);
      } else {
        console.error('🔍 [DEBUG] API returned success: false');
        toast.error('Lỗi khi tải dữ liệu');
      }
    } catch (error) {
      console.error('Error loading financial cases:', error);
      toast.error('Lỗi khi tải dữ liệu');
    } finally {
      setLoading(false);
    }
  };

  const calculateStatistics = (cases) => {
    const stats = {
      total: cases.length,
      approved: cases.filter(c => c.status === 'approved').length,
      rejected: cases.filter(c => c.status === 'rejected').length,
      totalAmountVND: 0,
      totalAmountUSD: 0,
      totalIncomeVND: 0,
      totalIncomeUSD: 0,
      totalExpenseVND: 0,
      totalExpenseUSD: 0,
      netProfitVND: 0,
      netProfitUSD: 0,
      pendingCasesVND: 0,
      pendingCasesUSD: 0,
      cancelledCasesVND: 0,
      cancelledCasesUSD: 0
    };

    // Tính toán theo từng case - CHỈ TÍNH APPROVED CASES
    cases.forEach(case_ => {
      // Chỉ tính approved cases vào thu chi
      if (case_.status !== 'approved') {
        return; // Bỏ qua rejected và pending cases
      }
      
      const amount = Math.abs(parseFloat(case_.amount) || 0);
      const currency = case_.currency || 'VND';
      
      if (currency === 'USD') {
        stats.totalAmountUSD += amount;
        if (case_.type === 'thu' || case_.type === 'income') {
          stats.totalIncomeUSD += amount;
        } else {
          stats.totalExpenseUSD += amount;
        }
      } else {
        stats.totalAmountVND += amount;
        if (case_.type === 'thu' || case_.type === 'income') {
          stats.totalIncomeVND += amount;
        } else {
          stats.totalExpenseVND += amount;
        }
      }
    });

    // Tính net profit
    stats.netProfitVND = stats.totalIncomeVND - stats.totalExpenseVND;
    stats.netProfitUSD = stats.totalIncomeUSD - stats.totalExpenseUSD;


    setStatistics(stats);
  };

  const calculatePendingCancelledStats = (allCases, filteredCases) => {
    // PENDING: Luôn hiển thị TẤT CẢ (không bị lọc theo thời gian)
    let pendingVND = allCases.filter(c => 
      c.status === 'pending' && 
      c.currency === 'VND'
    );
    
    let pendingUSD = allCases.filter(c => 
      c.status === 'pending' && 
      c.currency === 'USD'
    );
    
    // CANCELLED/REJECTED: Chỉ lọc theo thời gian (từ filteredCases)
    let cancelledVND = filteredCases.filter(c => 
      c.status === 'cancelled' && 
      c.currency === 'VND'
    );
    
    let cancelledUSD = filteredCases.filter(c => 
      c.status === 'cancelled' && 
      c.currency === 'USD'
    );
    
    let rejectedVND = filteredCases.filter(c => 
      c.status === 'rejected' && 
      c.currency === 'VND'
    );
    
    let rejectedUSD = filteredCases.filter(c => 
      c.status === 'rejected' && 
      c.currency === 'USD'
    );
    
    // Cập nhật statistics với pending/rejected counts
    setStatistics(prevStats => ({
      ...prevStats,
      pendingCasesVND: pendingVND.length,
      pendingCasesUSD: pendingUSD.length,
      cancelledCasesVND: rejectedVND.length, // Đã Hủy = Rejected VND
      cancelledCasesUSD: rejectedUSD.length   // Đã Hủy = Rejected USD
    }));
  };

  const calculateSellerStatistics = (cases) => {
    const sellerData = {};
    
    cases.forEach(case_ => {
      const sellerName = case_.seller_name || 'Unknown';
      const currency = case_.currency || 'VND';
      
      if (!sellerData[sellerName]) {
        sellerData[sellerName] = {
          name: sellerName,
          total: 0,
          approved: 0,
          rejected: 0,
          totalAmountVND: 0,
          totalAmountUSD: 0,
          incomeAmountVND: 0,
          incomeAmountUSD: 0,
          expenseAmountVND: 0,
          expenseAmountUSD: 0
        };
      }
      
      sellerData[sellerName].total++;
      sellerData[sellerName][case_.status]++;
      
      // CHỈ TÍNH TIỀN CHO APPROVED CASES
      if (case_.status === 'approved') {
        const amount = Math.abs(parseFloat(case_.amount) || 0);
        
        if (currency === 'USD') {
          sellerData[sellerName].totalAmountUSD += amount;
          if (case_.type === 'thu' || case_.type === 'income') {
            sellerData[sellerName].incomeAmountUSD += amount;
          } else {
            sellerData[sellerName].expenseAmountUSD += amount;
          }
        } else {
          sellerData[sellerName].totalAmountVND += amount;
          if (case_.type === 'thu' || case_.type === 'income') {
            sellerData[sellerName].incomeAmountVND += amount;
          } else {
            sellerData[sellerName].expenseAmountVND += amount;
          }
        }
      }
    });
    
    setSellerStats(Object.values(sellerData).sort((a, b) => b.total - a.total));
  };

  // Hàm tạo biểu đồ cho một loại tiền tệ cụ thể
  const generateChartDataForCurrency = (cases, currency) => {
    // Lọc cases theo tiền tệ
    const currencyCases = cases.filter(case_ => case_.currency === currency);

    // Tạo danh sách tất cả các ngày từ cases đã filter
    const allDates = new Set();
    currencyCases.forEach(case_ => {
      const date = new Date(case_.created_at || case_.date).toLocaleDateString('vi-VN');
      allDates.add(date);
    });

    // Sắp xếp ngày theo thứ tự
    const sortedDates = Array.from(allDates).sort((a, b) => new Date(a) - new Date(b));

    // Group by date và khởi tạo tất cả ngày với giá trị 0
    const dailyData = {};
    sortedDates.forEach(date => {
      dailyData[date] = { income: 0, expense: 0, count: 0 };
    });

    // Thêm dữ liệu thực tế - CHỈ TÍNH APPROVED CASES
    currencyCases.forEach(case_ => {
      // Chỉ tính approved cases vào biểu đồ
      if (case_.status !== 'approved') {
        return;
      }
      
      const date = new Date(case_.created_at || case_.date).toLocaleDateString('vi-VN');
      if (dailyData[date]) {
        const amount = Math.abs(parseFloat(case_.amount) || 0);
        if (case_.type === 'thu' || case_.type === 'income') {
          dailyData[date].income += amount;
        } else {
          dailyData[date].expense += amount;
        }
        dailyData[date].count++;
      }
    });

    const labels = Object.keys(dailyData);
    const incomeData = labels.map(date => dailyData[date].income);
    const expenseData = labels.map(date => dailyData[date].expense);

    return {
      labels,
      datasets: [
        {
          label: 'Thu nhập',
          data: incomeData,
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          fill: true,
          tension: 0.4
        },
        {
          label: 'Chi phí',
          data: expenseData,
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          fill: true,
          tension: 0.4
        }
      ]
    };
  };

  const generateChartData = (cases) => {
    // Tạo biểu đồ cho VND và USD
    const vndChartData = generateChartDataForCurrency(cases, 'VND');
    const usdChartData = generateChartDataForCurrency(cases, 'USD');
    
    setChartData({
      vnd: vndChartData,
      usd: usdChartData
    });
  };

  // Hàm tạo bộ lọc thời gian
  const getTimeFilter = () => {
    if (selectedTimeRange === 'all') return null;
    
    const now = new Date();
    let startDate;
    let endDate = now;

    switch (selectedTimeRange) {
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'week':
        const dayOfWeek = now.getDay();
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysFromMonday);
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), quarter * 3, 1);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate);
          endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59, 999);
        } else {
          startDate = null;
        }
        break;
      default:
        startDate = null;
    }

    return { startDate, endDate };
  };

  // Hàm áp dụng bộ lọc cho cases
  const applyFilters = (cases) => {
    let filtered = [...cases];

    // Filter theo seller
    if (selectedSellerForList && selectedSellerForList !== 'all') {
      filtered = filtered.filter(case_ => 
        case_.seller_name === selectedSellerForList
      );
    }

    // Filter theo search
    if (searchTerm) {
      filtered = filtered.filter(case_ =>
        case_.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        case_.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        case_.seller_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter theo status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(case_ => case_.status === statusFilter);
    }

    // Filter theo currency
    if (selectedCurrency !== 'all') {
      filtered = filtered.filter(case_ => case_.currency === selectedCurrency);
    }

    // Filter theo time range
    const timeFilter = getTimeFilter();
    if (timeFilter && timeFilter.startDate) {
      filtered = filtered.filter(case_ => {
        const caseDate = new Date(case_.created_at || case_.updated_at || case_.date);
        if (selectedTimeRange === 'custom') {
          return caseDate >= timeFilter.startDate && caseDate <= timeFilter.endDate;
        } else {
          return caseDate >= timeFilter.startDate;
        }
      });
    }

    return filtered;
  };

  // Hàm tính toán phân trang
  const getPaginatedData = (data) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  };

  // Hàm tính tổng số trang
  const calculateTotalPages = (totalItems) => {
    return Math.ceil(totalItems / itemsPerPage);
  };

  // Hàm chuyển trang
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Hàm thay đổi số items per page
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset về trang đầu
  };

  const filterCases = () => {
    // Áp dụng bộ lọc cho danh sách cases chính
    const filtered = applyFilters(financialCases);
    setFilteredCases(filtered);
    
    // Cập nhật phân trang
    const totalPagesCount = calculateTotalPages(filtered.length);
    setTotalPages(totalPagesCount);
    
    // Reset về trang đầu khi filter thay đổi
    setCurrentPage(1);
    
    // Cập nhật thống kê với dữ liệu đã filter (chỉ approved)
    const approvedCases = filtered.filter(case_ => case_.status === 'approved');
    calculateStatistics(approvedCases);
    calculateSellerStatistics(filtered); // Vẫn tính seller stats từ tất cả cases đã filter
    
    // Cập nhật pending/cancelled counts (pending = tất cả, cancelled/rejected = đã lọc)
    const filteredAllCases = applyFilters(allCases);
    calculatePendingCancelledStats(allCases, filteredAllCases);
  };

  useEffect(() => {
    loadFinancialCases();
  }, []);

  useEffect(() => {
    filterCases();
  }, [searchTerm, statusFilter, selectedTimeRange, selectedCurrency, financialCases, selectedSellerForList, customStartDate, customEndDate, itemsPerPage]);

  // Filter seller popup data when filters change
  useEffect(() => {
    if (showSellerDetailModal && sellerDetailData) {
      filterSellerPopupData();
    }
  }, [sellerPopupTimeRange, sellerPopupCurrency, sellerDetailData]);

  // Generate chart data when seller detail modal opens
  useEffect(() => {
    if (showSellerDetailModal && sellerDetailData && sellerDetailData.allCases) {
      // Generate chart data
    }
  }, [showSellerDetailModal, sellerDetailData]);

  useEffect(() => {
    if (filteredCases.length > 0) {
      generateChartData(filteredCases);
    }
  }, [selectedTimeRange, selectedCurrency, filteredCases, searchTerm, statusFilter, selectedSellerForList, customStartDate, customEndDate]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-3 h-3" />;
      case 'rejected':
        return <XCircle className="w-3 h-3" />;
      default:
        return <Clock className="w-3 h-3" />;
    }
  };

  const getTypeColor = (type) => {
    return (type === 'thu' || type === 'income') 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Không có ngày';
    
    try {
      const date = new Date(dateString);
      
      // Kiểm tra xem date có hợp lệ không
      if (isNaN(date.getTime())) {
        console.warn('Invalid date string:', dateString);
        return 'Ngày không hợp lệ';
      }
      
      return date.toLocaleDateString('vi-VN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error, 'Date string:', dateString);
      return 'Lỗi định dạng ngày';
    }
  };

  // Handle seller detail popup
  const handleSellerClick = (seller) => {
    setSelectedSeller(seller);
    
    // Filter cases for this specific seller from ALL cases (not filteredCases)
    const sellerCases = financialCases.filter(case_ => 
      case_.seller_name === seller.name
    );
    

    
    // Calculate seller-specific statistics
    const sellerStats = {
      name: seller.name,
      total: sellerCases.length,
      approved: sellerCases.filter(c => c.status === 'approved').length,
      rejected: sellerCases.filter(c => c.status === 'rejected').length,
      totalAmountVND: seller.totalAmountVND,
      totalAmountUSD: seller.totalAmountUSD,
      incomeAmountVND: seller.incomeAmountVND,
      incomeAmountUSD: seller.incomeAmountUSD,
      expenseAmountVND: seller.expenseAmountVND,
      expenseAmountUSD: seller.expenseAmountUSD,
      cases: sellerCases,
      allCases: sellerCases // Lưu tất cả cases để filter sau
    };
    
    setSellerDetailData(sellerStats);
    setSellerPopupTimeRange('all');
    setSellerPopupCurrency('all');
    setShowSellerDetailModal(true);
  };

  // Filter seller popup data
  const filterSellerPopupData = () => {
    if (!sellerDetailData || !sellerDetailData.allCases) {
      return;
    }
    
    const now = new Date();
    let startDate;
    let endDate = now;

    switch (sellerPopupTimeRange) {
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'week':
        const dayOfWeek = now.getDay();
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysFromMonday);
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), quarter * 3, 1);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      default:
        startDate = new Date(0); // All time
    }

    // Filter cases by time range and currency
    const filteredCases = sellerDetailData.allCases.filter(case_ => {
      const caseDate = new Date(case_.created_at || case_.date);
      const currency = case_.currency || 'VND';
      
      const timeMatch = caseDate >= startDate && caseDate <= endDate;
      const currencyMatch = sellerPopupCurrency === 'all' || currency === sellerPopupCurrency;
      
      return timeMatch && currencyMatch;
    });



    // Calculate new statistics
    const newStats = calculateStatistics(filteredCases);
    
    // Update seller detail data
    setSellerDetailData(prev => ({
      ...prev,
      ...newStats,
      cases: filteredCases
    }));
  };

  // Generate chart data for seller detail
  const generateSellerChartData = (cases) => {
    const now = new Date();
    let startDate;
    let endDate = now;

    switch (sellerPopupTimeRange) {
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'week':
        const dayOfWeek = now.getDay();
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysFromMonday);
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), quarter * 3, 1);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      default:
        startDate = new Date(0); // All time
    }

    // Filter cases by time range and currency
    const filteredCases = cases.filter(case_ => {
      const caseDate = new Date(case_.created_at || case_.date);
      const currency = case_.currency || 'VND';
      
      const timeMatch = caseDate >= startDate && caseDate <= endDate;
      const currencyMatch = sellerPopupCurrency === 'all' || currency === sellerPopupCurrency;
      
      return timeMatch && currencyMatch;
    });

    // Group by date - CHỈ TÍNH APPROVED CASES
    const groupedData = {};
    filteredCases.forEach(case_ => {
      // Chỉ tính approved cases vào biểu đồ
      if (case_.status !== 'approved') {
        return;
      }
      
      const date = new Date(case_.created_at || case_.date).toLocaleDateString('vi-VN');
      const currency = case_.currency || 'VND';
      
      if (!groupedData[date]) {
        groupedData[date] = { income: 0, expense: 0 };
      }
      
      const amount = Math.abs(parseFloat(case_.amount) || 0);
      
      if (case_.type === 'thu' || case_.type === 'income') {
        groupedData[date].income += amount;
      } else {
        groupedData[date].expense += amount;
      }
    });

    const dates = Object.keys(groupedData).sort();
    const incomeData = dates.map(date => groupedData[date].income);
    const expenseData = dates.map(date => groupedData[date].expense);

    return {
      labels: dates,
      datasets: [
        {
          label: 'Thu nhập',
          data: incomeData,
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          fill: true,
          tension: 0.4
        },
        {
          label: 'Chi phí',
          data: expenseData,
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          fill: true,
          tension: 0.4
        }
      ]
    };
  };

  // Hàm tính toán cỡ chữ động theo độ dài số tiền
  const getDynamicFontSize = (amount, currency = 'VND', isMainAmount = false) => {
    const formattedAmount = formatCurrency(amount, currency);
    const length = formattedAmount.length;
    
    // Cỡ chữ cơ bản
    let baseSize = isMainAmount ? 'text-2xl' : 'text-sm';
    
    // Điều chỉnh theo độ dài
    if (length <= 8) {
      return isMainAmount ? 'text-3xl' : 'text-base';
    } else if (length <= 12) {
      return isMainAmount ? 'text-2xl' : 'text-sm';
    } else if (length <= 16) {
      return isMainAmount ? 'text-xl' : 'text-xs';
    } else if (length <= 20) {
      return isMainAmount ? 'text-lg' : 'text-xs';
    } else {
      return isMainAmount ? 'text-base' : 'text-xs';
    }
  };

  // Hàm debug để kiểm tra API response
  const debugApiResponse = (response) => {
    // Debug function - removed console.logs
  };

  // Hàm format ngày cho CSV - đảm bảo Excel nhận diện được
  const formatDateForCSV = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      // Format: YYYY-MM-DD HH:mm:ss (ISO format) - Excel chắc chắn nhận diện được
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      console.error('Lỗi format ngày:', error);
      return '';
    }
  };

  // Hàm xuất dữ liệu CSV với đầy đủ thông tin và không bị lỗi font
  const exportToCSV = () => {
    try {
      // Tạo header cho CSV
      const headers = [
        'ID',
        'Tiêu đề',
        'Mô tả', 
        'Seller',
        'Loại',
        'Số tiền',
        'Tiền tệ',
        'Trạng thái',
        'Ngày tạo (YYYY-MM-DD)',
        'Giờ tạo (HH:mm:ss)',
        'Ngày cập nhật (YYYY-MM-DD)',
        'Giờ cập nhật (HH:mm:ss)',
        'Ghi chú'
      ];

      // Hàm tách ngày và giờ
      const splitDateAndTime = (dateString) => {
        if (!dateString) return ['', ''];
        
        try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return ['', ''];
          
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          const seconds = String(date.getSeconds()).padStart(2, '0');
          
          const datePart = `${year}-${month}-${day}`;
          const timePart = `${hours}:${minutes}:${seconds}`;
          
          return [datePart, timePart];
        } catch (error) {
          return ['', ''];
        }
      };

      // Chuyển đổi dữ liệu thành CSV
      const csvData = filteredCases.map(case_ => {
        const [createdDate, createdTime] = splitDateAndTime(case_.created_at || case_.date || '');
        const [updatedDate, updatedTime] = splitDateAndTime(case_.updated_at || case_.created_at || case_.date || '');
        
        return [
          case_.id || '',
          `"${(case_.title || '').replace(/"/g, '""')}"`, // Escape quotes
          `"${(case_.description || '').replace(/"/g, '""')}"`,
          `"${(case_.seller_name || '').replace(/"/g, '""')}"`,
          case_.type === 'income' || case_.type === 'thu' ? 'Thu' : 'Chi',
          case_.amount || 0,
          case_.currency || 'VND',
          case_.status === 'approved' ? 'Đã duyệt' : case_.status === 'rejected' ? 'Đã từ chối' : 'Chờ duyệt',
          createdDate,
          createdTime,
          updatedDate,
          updatedTime,
          `"${(case_.note || '').replace(/"/g, '""')}"`
        ];
      });

      // Thêm BOM để đảm bảo Excel hiển thị đúng tiếng Việt
      const BOM = '\uFEFF';
      const csvContent = BOM + [headers, ...csvData]
        .map(row => row.join(','))
        .join('\n');

      // Tạo blob và download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      // Tạo tên file với timestamp
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const sellerName = selectedSellerForList === 'all' ? 'TatCaSeller' : selectedSellerForList.replace(/\s+/g, '_');
      const fileName = `Cases_${sellerName}_${timestamp}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Đã xuất ${filteredCases.length} cases thành công!`);
    } catch (error) {
      console.error('Lỗi khi xuất CSV:', error);
      toast.error('Có lỗi xảy ra khi xuất dữ liệu!');
    }
  };

  // Hàm tạo chart options cho từng loại tiền tệ
  const getChartOptions = (currency) => {
    return {
      responsive: true,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: `Thu chi ${currency} theo ${selectedTimeRange === 'today' ? 'giờ' : 'ngày'}`,
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              const label = context.dataset.label || '';
              const value = context.parsed.y;
              return `${label}: ${new Intl.NumberFormat(currency === 'USD' ? 'en-US' : 'vi-VN', {
                style: 'currency',
                currency: currency,
                minimumFractionDigits: currency === 'USD' ? 2 : 0,
                maximumFractionDigits: currency === 'USD' ? 2 : 0
              }).format(value)}`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            callback: function(value) {
              return new Intl.NumberFormat(currency === 'USD' ? 'en-US' : 'vi-VN', {
                style: 'currency',
                currency: currency,
                minimumFractionDigits: currency === 'USD' ? 2 : 0,
                maximumFractionDigits: currency === 'USD' ? 2 : 0
              }).format(value);
            }
          }
        }
      }
    };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-400/20 to-pink-400/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-blue-300/10 to-purple-300/10 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      {/* Header */}
      <div className="relative bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/90 via-purple-600/90 to-indigo-700/90 backdrop-blur-sm"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="mb-8 lg:mb-0 animate-fade-in-up">
              <div className="flex items-center mb-4">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-400 rounded-2xl blur-lg opacity-75 animate-pulse"></div>
                  <div className="relative bg-gradient-to-r from-blue-500 to-purple-500 p-3 rounded-2xl shadow-2xl">
                    <Activity className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div className="ml-4">
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">
                    Thống kê & Cases đã xử lý
                  </h1>
                  <div className="flex items-center mt-2 space-x-2">
                    <Sparkles className="w-4 h-4 text-yellow-300 animate-pulse" />
                    <span className="text-blue-100 text-lg font-medium">
                      Xem thống kê chi tiết và các cases đã được approve/reject
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4 animate-fade-in-up delay-200">
              <button
                onClick={() => setShowCharts(!showCharts)}
                className="group relative flex items-center space-x-3 bg-white/10 backdrop-blur-sm text-white px-6 py-3 rounded-2xl hover:bg-white/20 transition-all duration-300 border border-white/20 hover:border-white/40 hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl blur-sm group-hover:blur-md transition-all duration-300"></div>
                <BarChart3 className="w-5 h-5 relative z-10 group-hover:scale-110 transition-transform duration-300" />
                <span className="relative z-10 font-medium">{showCharts ? 'Ẩn' : 'Hiện'} Biểu đồ</span>
              </button>
              
              <button
                onClick={loadFinancialCases}
                className="group relative flex items-center space-x-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-6 py-3 rounded-2xl hover:from-emerald-600 hover:to-teal-600 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
              >
                <RefreshCw className={`w-5 h-5 group-hover:rotate-180 transition-transform duration-500 ${loading ? 'animate-spin' : ''}`} />
                <span className="font-medium">Làm mới</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistics Cards - New Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6 lg:mb-8">
          {/* Cột 1 - VND Income/Expense */}
          <div className="space-y-4">
            {/* VND Income */}
            <div className="group relative bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 border border-white/20 hover:border-green-200/50 hover:scale-105 animate-fade-in-up">
              <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 to-emerald-50/50 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">Tổng Thu VND</p>
                                         <p className={`${getDynamicFontSize(statistics.totalIncomeVND, 'VND', true)} font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent`}>
                       {formatCurrency(statistics.totalIncomeVND, 'VND')}
                     </p>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-400 rounded-xl blur-lg opacity-50 group-hover:opacity-75 transition-opacity duration-300"></div>
                    <div className="relative p-3 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <TrendingUpIcon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* VND Expense */}
            <div className="group relative bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 border border-white/20 hover:border-red-200/50 hover:scale-105 animate-fade-in-up delay-100">
              <div className="absolute inset-0 bg-gradient-to-br from-red-50/50 to-pink-50/50 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">Tổng Chi VND</p>
                                         <p className={`${getDynamicFontSize(statistics.totalExpenseVND, 'VND', true)} font-bold bg-gradient-to-r from-red-600 to-pink-600 bg-clip-text text-transparent`}>
                       {formatCurrency(statistics.totalExpenseVND, 'VND')}
                     </p>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-red-400 to-pink-400 rounded-xl blur-lg opacity-50 group-hover:opacity-75 transition-opacity duration-300"></div>
                    <div className="relative p-3 bg-gradient-to-r from-red-500 to-pink-500 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <TrendingDownIcon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Cột 2 - USD Income/Expense */}
          <div className="space-y-4">
            {/* USD Income */}
            <div className="group relative bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 border border-white/20 hover:border-green-200/50 hover:scale-105 animate-fade-in-up delay-200">
              <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 to-emerald-50/50 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">Tổng Thu USD</p>
                                         <p className={`${getDynamicFontSize(statistics.totalIncomeUSD, 'USD', true)} font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent`}>
                       {formatCurrency(statistics.totalIncomeUSD, 'USD')}
                     </p>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-400 rounded-xl blur-lg opacity-50 group-hover:opacity-75 transition-opacity duration-300"></div>
                    <div className="relative p-3 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <TrendingUpIcon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* USD Expense */}
            <div className="group relative bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 border border-white/20 hover:border-red-200/50 hover:scale-105 animate-fade-in-up delay-300">
              <div className="absolute inset-0 bg-gradient-to-br from-red-50/50 to-pink-50/50 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">Tổng Chi USD</p>
                                         <p className={`${getDynamicFontSize(statistics.totalExpenseUSD, 'USD', true)} font-bold bg-gradient-to-r from-red-600 to-pink-600 bg-clip-text text-transparent`}>
                       {formatCurrency(statistics.totalExpenseUSD, 'USD')}
                     </p>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-red-400 to-pink-400 rounded-xl blur-lg opacity-50 group-hover:opacity-75 transition-opacity duration-300"></div>
                    <div className="relative p-3 bg-gradient-to-r from-red-500 to-pink-500 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <TrendingDownIcon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Cột 3 - VND Summary */}
          <div className="space-y-4">
                                      {/* VND Net Profit with Pending & Cancelled */}
             <div className="group relative bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 border border-white/20 hover:border-blue-200/50 hover:scale-105 animate-fade-in-up delay-400 h-full flex flex-col">
               <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-indigo-50/50 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
               <div className="relative z-10 flex flex-col h-full">
                 <div className="flex items-center justify-between mb-4">
                   <div>
                     <p className="text-sm font-medium text-gray-600 mb-1">Tổng Thu Chi VND</p>
                     <p className={`${getDynamicFontSize(statistics.netProfitVND, 'VND', true)} font-bold ${statistics.netProfitVND >= 0 ? 'bg-gradient-to-r from-green-600 to-emerald-600' : 'bg-gradient-to-r from-red-600 to-pink-600'} bg-clip-text text-transparent`}>
                       {formatCurrency(statistics.netProfitVND, 'VND')}
                     </p>
                   </div>
                   <div className="relative">
                     <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-xl blur-lg opacity-50 group-hover:opacity-75 transition-opacity duration-300"></div>
                     <div className="relative p-3 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                       <DollarSign className="w-5 h-5 text-white" />
                     </div>
                   </div>
                 </div>
                 <div className="space-y-1 mb-3">
                   <div className="flex items-center text-xs">
                     <TrendingUpIcon className="w-3 h-3 text-green-500 mr-1" />
                     <span className="text-green-600 font-medium">
                       Thu: {formatCurrency(statistics.totalIncomeVND, 'VND')}
                     </span>
                   </div>
                   <div className="flex items-center text-xs">
                     <TrendingDownIcon className="w-3 h-3 text-red-500 mr-1" />
                     <span className="text-red-600 font-medium">
                       Chi: {formatCurrency(statistics.totalExpenseVND, 'VND')}
                     </span>
                   </div>
                 </div>
                 
                 {/* VND Pending & Cancelled inside the same card */}
                 <div className="grid grid-cols-2 gap-3 mt-auto">
                   {/* VND Pending */}
                   <div className="bg-gradient-to-r from-yellow-50/80 to-orange-50/80 rounded-xl p-2 border border-yellow-200/50">
                     <div className="text-center">
                       <p className="text-xs font-medium text-gray-600 mb-1">Cần Duyệt</p>
                       <p className="text-lg font-bold bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">
                         {statistics.pendingCasesVND} cases
                       </p>
                     </div>
                   </div>

                   {/* VND Cancelled */}
                   <div className="bg-gradient-to-r from-gray-50/80 to-slate-50/80 rounded-xl p-2 border border-gray-200/50">
                     <div className="text-center">
                       <p className="text-xs font-medium text-gray-600 mb-1">Đã Hủy</p>
                       <p className="text-lg font-bold bg-gradient-to-r from-gray-600 to-slate-600 bg-clip-text text-transparent">
                         {statistics.cancelledCasesVND} cases
                       </p>
                     </div>
                   </div>
                 </div>
               </div>
             </div>
          </div>

          {/* Cột 4 - USD Summary */}
          <div className="space-y-4">
                                      {/* USD Net Profit with Pending & Cancelled */}
             <div className="group relative bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 border border-white/20 hover:border-blue-200/50 hover:scale-105 animate-fade-in-up delay-700 h-full flex flex-col">
               <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-indigo-50/50 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
               <div className="relative z-10 flex flex-col h-full">
                 <div className="flex items-center justify-between mb-4">
                   <div>
                     <p className="text-sm font-medium text-gray-600 mb-1">Tổng Thu Chi USD</p>
                     <p className={`${getDynamicFontSize(statistics.netProfitUSD, 'USD', true)} font-bold ${statistics.netProfitUSD >= 0 ? 'bg-gradient-to-r from-green-600 to-emerald-600' : 'bg-gradient-to-r from-red-600 to-pink-600'} bg-clip-text text-transparent`}>
                       {formatCurrency(statistics.netProfitUSD, 'USD')}
                     </p>
                   </div>
                   <div className="relative">
                     <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-xl blur-lg opacity-50 group-hover:opacity-75 transition-opacity duration-300"></div>
                     <div className="relative p-3 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                       <DollarSign className="w-5 h-5 text-white" />
                     </div>
                   </div>
                 </div>
                 <div className="space-y-1 mb-3">
                   <div className="flex items-center text-xs">
                     <TrendingUpIcon className="w-3 h-3 text-green-500 mr-1" />
                     <span className="text-green-600 font-medium">
                       Thu: {formatCurrency(statistics.totalIncomeUSD, 'USD')}
                     </span>
                   </div>
                   <div className="flex items-center text-xs">
                     <TrendingDownIcon className="w-3 h-3 text-red-500 mr-1" />
                     <span className="text-red-600 font-medium">
                       Chi: {formatCurrency(statistics.totalExpenseUSD, 'USD')}
                     </span>
                   </div>
                 </div>
                 
                 {/* USD Pending & Cancelled inside the same card */}
                 <div className="grid grid-cols-2 gap-3 mt-auto">
                   {/* USD Pending */}
                   <div className="bg-gradient-to-r from-yellow-50/80 to-orange-50/80 rounded-xl p-2 border border-yellow-200/50">
                     <div className="text-center">
                       <p className="text-xs font-medium text-gray-600 mb-1">Cần Duyệt</p>
                       <p className="text-lg font-bold bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">
                         {statistics.pendingCasesUSD} cases
                       </p>
                     </div>
                   </div>

                   {/* USD Cancelled */}
                   <div className="bg-gradient-to-r from-gray-50/80 to-slate-50/80 rounded-xl p-2 border border-gray-200/50">
                     <div className="text-center">
                       <p className="text-xs font-medium text-gray-600 mb-1">Đã Hủy</p>
                       <p className="text-lg font-bold bg-gradient-to-r from-gray-600 to-slate-600 bg-clip-text text-transparent">
                         {statistics.cancelledCasesUSD} cases
                       </p>
                     </div>
                   </div>
                 </div>
               </div>
             </div>
          </div>
        </div>

        {/* Filters */}
        <div className="group relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 mb-6 border border-white/20 hover:border-blue-200/50 transition-all duration-500 animate-fade-in-up delay-400">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-indigo-50/30 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10">
            <div className="flex items-center mb-6">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-xl blur-lg opacity-50"></div>
                    <div className="relative p-2 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl">
                  <Filter className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <h3 className="ml-3 text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Bộ lọc & Tìm kiếm
                  </h3>
                </div>
                
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div className="group/select">
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <User className="w-4 h-4 mr-2" />
                  Seller
                </label>
                <select
                  value={selectedSellerForList}
                  onChange={(e) => setSelectedSellerForList(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:border-blue-400 group-hover/select:shadow-lg bg-white/80 backdrop-blur-sm"
                >
                  <option value="all">Tất cả Seller</option>
                  {sellerStats.map((seller, index) => (
                    <option key={index} value={seller.name}>{seller.name}</option>
                  ))}
                </select>
              </div>

              <div className="group/input">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Search className="w-4 h-4 mr-2" />
                  Tìm kiếm
                </label>
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 group-hover/input:text-blue-500 transition-colors duration-300" />
                  <input
                    type="text"
                    placeholder="Tìm theo tên, mô tả..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:border-blue-400 group-hover/input:shadow-lg bg-white/80 backdrop-blur-sm"
                  />
                </div>
              </div>

              <div className="group/select">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Trạng thái
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:border-blue-400 group-hover/select:shadow-lg bg-white/80 backdrop-blur-sm"
                >
                  <option value="all">Tất cả trạng thái</option>
                  <option value="approved">Đã duyệt</option>
                  <option value="rejected">Đã từ chối</option>
                </select>
              </div>

              <div className="group/select">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  Thời gian
                </label>
                <select
                  value={selectedTimeRange}
                  onChange={(e) => setSelectedTimeRange(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:border-blue-400 group-hover/select:shadow-lg bg-white/80 backdrop-blur-sm"
                >
                  <option value="all">Tất cả thời gian</option>
                  <option value="today">Hôm nay</option>
                  <option value="week">Tuần này</option>
                  <option value="month">Tháng này</option>
                  <option value="quarter">Quý này</option>
                  <option value="year">Năm nay</option>
                  <option value="custom">Tùy chỉnh</option>
                </select>
              </div>

              {/* Custom Date Range - Hiển thị khi chọn "Tùy chỉnh" */}
              {selectedTimeRange === 'custom' && (
                <div className="col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="group/input">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <CalendarDays className="w-4 h-4 mr-2" />
                      Từ ngày
                    </label>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:border-blue-400 group-hover/input:shadow-lg bg-white/80 backdrop-blur-sm"
                    />
                  </div>
                  
                  <div className="group/input">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <CalendarDays className="w-4 h-4 mr-2" />
                      Đến ngày
                    </label>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:border-blue-400 group-hover/input:shadow-lg bg-white/80 backdrop-blur-sm"
                    />
                  </div>
                </div>
              )}

                    <div className="group/select">
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                        <DollarSign className="w-4 h-4 mr-2" />
                        Tiền tệ
                      </label>
                      <select
                        value={selectedCurrency}
                        onChange={(e) => setSelectedCurrency(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:border-blue-400 group-hover/select:shadow-lg bg-white/80 backdrop-blur-sm"
                      >
                  <option value="all">Tất cả tiền tệ</option>
                        <option value="VND">VND</option>
                        <option value="USD">USD</option>
                      </select>
                  </div>
                </div>
                
                {/* Reset Filters Button */}
                <div className="flex justify-end mt-4">
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setStatusFilter('all');
                      setSelectedTimeRange('all');
                      setSelectedCurrency('all');
                      setSelectedSellerForList('all');
                      setCustomStartDate('');
                      setCustomEndDate('');
                    }}
                    className="group relative flex items-center space-x-2 bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 rounded-xl hover:from-orange-600 hover:to-red-600 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-orange-400/20 to-red-400/20 rounded-xl blur-sm group-hover:blur-md transition-all duration-300"></div>
                    <RefreshCw className="w-4 h-4 relative z-10 group-hover:rotate-180 transition-transform duration-500" />
                    <span className="relative z-10 font-medium">Reset Filters</span>
                  </button>
                </div>
              </div>
            </div>

        {/* Charts Section */}
        {showCharts && (
          <div className="mb-8 animate-fade-in-up delay-500">
            {/* Charts Header */}
            <div className="flex items-center mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-xl blur-lg opacity-50"></div>
                <div className="relative p-2 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl">
                  <BarChart3 className="w-5 h-5 text-white" />
                </div>
              </div>
              <h3 className="ml-3 text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Biểu đồ Thống kê Thu Chi
              </h3>
            </div>

            {/* Dual Charts */}
            {chartData && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* VND Chart */}
                <div className="group relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/20 hover:border-green-200/50 transition-all duration-500">
                  <div className="absolute inset-0 bg-gradient-to-br from-green-50/30 to-emerald-50/30 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="relative z-10">
                    <div className="flex items-center mb-4">
                      <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-400 rounded-xl blur-lg opacity-50"></div>
                        <div className="relative p-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl">
                          <DollarSign className="w-5 h-5 text-white" />
                        </div>
                      </div>
                      <h4 className="ml-3 text-lg font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                        Biểu đồ VND
                      </h4>
                    </div>
                    <div className="h-80">
                      <Line 
                        data={chartData.vnd} 
                        options={getChartOptions('VND')} 
                      />
                    </div>
                  </div>
                </div>

                {/* USD Chart */}
                <div className="group relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/20 hover:border-blue-200/50 transition-all duration-500">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-indigo-50/30 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="relative z-10">
                    <div className="flex items-center mb-4">
                      <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-xl blur-lg opacity-50"></div>
                        <div className="relative p-2 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl">
                          <DollarSign className="w-5 h-5 text-white" />
                        </div>
                      </div>
                      <h4 className="ml-3 text-lg font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                        Biểu đồ USD
                      </h4>
                    </div>
                    <div className="h-80">
                      <Line 
                        data={chartData.usd} 
                        options={getChartOptions('USD')} 
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Seller Statistics */}
        {sellerStats.length > 0 && (
          <div className="group relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 mb-8 border border-white/20 hover:border-blue-200/50 transition-all duration-500 animate-fade-in-up delay-500">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-indigo-50/30 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative z-10">
              <div className="flex items-center mb-6">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-xl blur-lg opacity-50"></div>
                  <div className="relative p-2 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                </div>
                <h3 className="ml-3 text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  Thống kê theo Seller
                </h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sellerStats.map((seller, index) => (
                  <div 
                    key={index} 
                    className="group/seller relative bg-gradient-to-br from-gray-50/80 to-white/80 backdrop-blur-sm rounded-2xl p-6 hover:shadow-2xl transition-all duration-500 cursor-pointer transform hover:scale-105 border border-gray-200/50 hover:border-blue-300/50 animate-fade-in-up"
                    style={{ animationDelay: `${index * 100}ms` }}
                    onClick={() => handleSellerClick(seller)}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-indigo-50/50 rounded-2xl opacity-0 group-hover/seller:opacity-100 transition-opacity duration-500"></div>
                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center">
                          <div className="relative">
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-full blur-lg opacity-50 group-hover/seller:opacity-75 transition-opacity duration-300"></div>
                            <div className="relative w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center">
                              <User className="w-4 h-4 text-white" />
                            </div>
                          </div>
                          <h4 className="ml-3 font-bold text-gray-900 group-hover/seller:text-blue-600 transition-colors duration-300">{seller.name}</h4>
                        </div>
                        <span className="text-sm text-gray-500 bg-white/50 px-2 py-1 rounded-full">{seller.total} cases</span>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 flex items-center">
                            <CheckCircle className="w-3 h-3 mr-1 text-green-500" />
                            Đã duyệt:
                          </span>
                          <span className="font-bold text-green-600">{seller.approved}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 flex items-center">
                            <XCircle className="w-3 h-3 mr-1 text-red-500" />
                            Đã từ chối:
                          </span>
                          <span className="font-bold text-red-600">{seller.rejected}</span>
                        </div>
                        
                        <div className="border-t border-gray-200/50 pt-3 space-y-2">
                          {seller.totalAmountVND > 0 && (
                            <p className="font-bold text-gray-900 flex items-center">
                              <DollarSign className="w-3 h-3 mr-1 text-gray-600" />
                              {formatCurrency(seller.totalAmountVND, 'VND')}
                            </p>
                          )}
                          {seller.totalAmountUSD > 0 && (
                            <p className="font-bold text-blue-600 flex items-center">
                              <DollarSign className="w-3 h-3 mr-1" />
                              {formatCurrency(seller.totalAmountUSD, 'USD')}
                            </p>
                          )}
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span>{seller.approved}/{seller.total} approved</span>
                            <div className="w-12 bg-gray-200 rounded-full h-1">
                              <div 
                                className="bg-gradient-to-r from-green-500 to-emerald-500 h-1 rounded-full transition-all duration-1000"
                                style={{ width: `${seller.total > 0 ? (seller.approved / seller.total) * 100 : 0}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}



        {/* Cases Table */}
        <div className="group relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden border border-white/20 hover:border-blue-200/50 transition-all duration-500 animate-fade-in-up delay-700">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-indigo-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          <div className="relative z-10">
            <div className="px-6 py-6 border-b border-gray-200/50 bg-gradient-to-r from-gray-50/80 to-blue-50/80 backdrop-blur-sm">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                    Danh sách Cases của {selectedSellerForList === 'all' ? 'Tất cả Seller' : selectedSellerForList}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1 flex items-center">
                    <FileText className="w-4 h-4 mr-2" />
                    Hiển thị {filteredCases.length} cases
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  {/* Nút xuất CSV */}
                  {filteredCases.length > 0 && (
                    <button
                      onClick={exportToCSV}
                      className="group relative flex items-center space-x-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-2 rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
                      title="Xuất tất cả cases ra file CSV"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-teal-400 rounded-xl blur-sm opacity-0 group-hover:opacity-50 transition-opacity duration-300"></div>
                      <Download className="w-4 h-4 relative z-10 group-hover:scale-110 transition-transform duration-300" />
                      <span className="relative z-10 text-sm font-medium">Xuất CSV</span>
                    </button>
                  )}
                  
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-sm text-green-600 font-medium">Đang hoạt động</span>
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="text-center">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-full blur-lg opacity-50 animate-pulse"></div>
                    <div className="relative bg-gradient-to-r from-blue-500 to-indigo-500 p-4 rounded-full">
                      <RefreshCw className="w-8 h-8 text-white animate-spin" />
                    </div>
                  </div>
                  <p className="text-gray-600 mt-4 font-medium">Đang tải dữ liệu...</p>
                  <div className="mt-2 w-32 bg-gray-200 rounded-full h-1">
                    <div className="bg-gradient-to-r from-blue-500 to-indigo-500 h-1 rounded-full animate-pulse"></div>
                  </div>
                </div>
              </div>
            ) : filteredCases.length === 0 ? (
              <div className="flex items-center justify-center py-16">
                <div className="text-center">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-gray-400 to-gray-500 rounded-full blur-lg opacity-50"></div>
                    <div className="relative bg-gradient-to-r from-gray-500 to-gray-600 p-4 rounded-full">
                      <FileText className="w-12 h-12 text-white" />
                    </div>
                  </div>
                  <p className="text-gray-600 text-lg font-medium mt-4">Không có cases nào</p>
                  <p className="text-gray-400 mt-2">Chưa có cases nào được xử lý hoặc không tìm thấy kết quả phù hợp</p>
                </div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200/50">
                    <thead className="bg-gradient-to-r from-gray-50/80 to-blue-50/80 backdrop-blur-sm">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                          Thông tin Case
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                          Loại & Số tiền
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                          Trạng thái
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                          Thao tác
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white/50 backdrop-blur-sm divide-y divide-gray-200/50">
                      {getPaginatedData(filteredCases).map((case_, index) => (
                        <tr 
                          key={case_.id} 
                          className="hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-indigo-50/50 transition-all duration-300 group/row animate-fade-in-up"
                          style={{ animationDelay: `${index * 50}ms` }}
                        >
                          <td className="px-6 py-6">
                            <div>
                              <div className="text-sm font-bold text-gray-900 line-clamp-1 group-hover/row:text-blue-600 transition-colors duration-300">
                                {case_.title}
                              </div>
                              <div className="text-sm text-gray-600 line-clamp-2 mt-2">
                                {case_.description}
                              </div>
                              <div className="flex items-center mt-3 text-xs text-gray-500">
                                <div className="relative">
                                  <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-full blur-sm opacity-50"></div>
                                  <div className="relative w-4 h-4 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center">
                                    <User className="w-2 h-2 text-white" />
                                  </div>
                                </div>
                                <span className="font-medium ml-2">{case_.seller_name}</span>
                                <span className="mx-2">•</span>
                                <span>{formatDate(case_.created_at || case_.updated_at || case_.date)}</span>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-6">
                            <div className="flex items-center space-x-3">
                              <span className={`inline-flex items-center px-3 py-1 text-xs font-bold rounded-full ${getTypeColor(case_.type)} shadow-sm`}>
                                {(case_.type === 'income' || case_.type === 'thu') ? (
                                  <>
                                    <TrendingUpIcon className="w-3 h-3 mr-1" />
                                    Thu
                                  </>
                                ) : (
                                  <>
                                    <TrendingDownIcon className="w-3 h-3 mr-1" />
                                    Chi
                                  </>
                                )}
                              </span>
                              <div className="text-right">
                                <span className="text-sm font-bold text-gray-900">
                                  {formatCurrency(case_.amount, case_.currency)}
                                </span>
                                <div className="text-xs text-gray-500">
                                  {case_.currency}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-6">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border-2 ${getStatusColor(case_.status)} shadow-sm`}>
                              {getStatusIcon(case_.status)}
                              <span className="ml-1">
                                {case_.status === 'approved' && 'Đã duyệt'}
                                {case_.status === 'rejected' && 'Đã từ chối'}
                              </span>
                            </span>
                          </td>
                          <td className="px-6 py-6">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => {
                                  setSelectedCase(case_);
                                  setShowDetailModal(true);
                                }}
                                className="group/btn relative p-3 text-blue-600 hover:text-blue-800 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 rounded-xl transition-all duration-300 shadow-sm hover:shadow-lg"
                                title="Xem chi tiết"
                              >
                                <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-xl blur-sm opacity-0 group-hover/btn:opacity-50 transition-opacity duration-300"></div>
                                <Eye className="w-4 h-4 relative z-10 group-hover/btn:scale-110 transition-transform duration-300" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                <div className="px-6 py-4 border-t border-gray-200/50 bg-gradient-to-r from-gray-50/80 to-blue-50/80 backdrop-blur-sm">
                  <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                    {/* Items per page selector */}
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">Hiển thị:</span>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                        className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/80 backdrop-blur-sm"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                      <span className="text-sm text-gray-600">dòng mỗi trang</span>
                    </div>
                    
                    {/* Pagination info */}
                    <div className="text-sm text-gray-600">
                      Hiển thị {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filteredCases.length)} trong tổng số {filteredCases.length} cases
                    </div>
                    
                    {/* Pagination controls */}
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 bg-white/80 backdrop-blur-sm"
                      >
                        Trước
                      </button>
                      
                      {/* Page numbers */}
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`px-3 py-1 text-sm rounded-lg transition-all duration-200 ${
                                currentPage === pageNum
                                  ? 'bg-blue-500 text-white'
                                  : 'border border-gray-300 hover:bg-blue-50 hover:border-blue-300 bg-white/80 backdrop-blur-sm'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 bg-white/80 backdrop-blur-sm"
                      >
                        Sau
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedCase && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[60] p-4 animate-fade-in">
          <div className="relative bg-white/95 backdrop-blur-sm rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-white/20 animate-scale-in">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-indigo-50/50 rounded-3xl"></div>
            <div className="relative z-10">
              <div className="p-8 border-b border-gray-200/50 bg-gradient-to-r from-gray-50/80 to-blue-50/80 backdrop-blur-sm rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-2xl blur-lg opacity-50"></div>
                      <div className="relative bg-gradient-to-r from-blue-500 to-indigo-500 p-3 rounded-2xl shadow-lg">
                        <FileText className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                        Chi tiết Financial Case
                      </h3>
                      <p className="text-sm text-gray-600 mt-1 flex items-center">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                          ID: #{selectedCase.id}
                        </span>
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="group relative p-3 text-gray-400 hover:text-gray-600 hover:bg-gray-100/80 rounded-2xl transition-all duration-300 hover:scale-110"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-gray-400 to-gray-500 rounded-2xl blur-sm opacity-0 group-hover:opacity-50 transition-opacity duration-300"></div>
                    <XCircle className="w-6 h-6 relative z-10" />
                  </button>
                </div>
              </div>

              <div className="p-8 space-y-8">
                {/* Main Info Card */}
                <div className="group relative bg-gradient-to-r from-blue-50/80 to-indigo-50/80 backdrop-blur-sm rounded-2xl p-6 border border-blue-200/50 hover:border-blue-300/50 transition-all duration-300">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-100/20 to-indigo-100/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-6">
                      <h4 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Thông tin chính</h4>
                      <span className={`inline-flex items-center px-4 py-2 text-sm font-bold rounded-full shadow-sm ${getTypeColor(selectedCase.type)}`}>
                        {(selectedCase.type === 'income' || selectedCase.type === 'thu') ? (
                          <>
                            <TrendingUpIcon className="w-4 h-4 mr-2" />
                            Thu nhập
                          </>
                        ) : (
                          <>
                            <TrendingDownIcon className="w-4 h-4 mr-2" />
                            Chi phí
                          </>
                        )}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <h5 className="text-lg font-bold text-gray-900 mb-3">{selectedCase.title}</h5>
                        <p className="text-gray-600 text-sm leading-relaxed">{selectedCase.description}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-900 bg-clip-text text-transparent mb-2">
                          {formatCurrency(selectedCase.amount, selectedCase.currency)}
                        </div>
                        <div className="text-sm text-gray-500 bg-white/50 px-3 py-1 rounded-full inline-block">
                          {selectedCase.currency}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="group relative bg-gradient-to-br from-gray-50/80 to-white/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 hover:border-gray-300/50 transition-all duration-300">
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-100/20 to-white/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="relative z-10">
                      <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                        <div className="relative">
                          <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-lg blur-sm opacity-50"></div>
                          <div className="relative p-2 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg">
                            <User className="w-4 h-4 text-white" />
                          </div>
                        </div>
                        <span className="ml-3">Thông tin Seller</span>
                      </h4>
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium text-gray-500">Tên Seller</label>
                          <p className="text-sm font-bold text-gray-900">{selectedCase.seller_name}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500">Email</label>
                          <p className="text-sm text-gray-900">{selectedCase.seller_email || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="group relative bg-gradient-to-br from-gray-50/80 to-white/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 hover:border-gray-300/50 transition-all duration-300">
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-100/20 to-white/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="relative z-10">
                      <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                        <div className="relative">
                          <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-400 rounded-lg blur-sm opacity-50"></div>
                          <div className="relative p-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg">
                            <Calendar className="w-4 h-4 text-white" />
                          </div>
                        </div>
                        <span className="ml-3">Thông tin thời gian</span>
                      </h4>
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium text-gray-500">Ngày tạo</label>
                          <p className="text-sm font-bold text-gray-900">{formatDate(selectedCase.created_at || selectedCase.updated_at || selectedCase.date)}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500">Trạng thái</label>
                          <div className="mt-2">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border-2 shadow-sm ${getStatusColor(selectedCase.status)}`}>
                              {getStatusIcon(selectedCase.status)}
                              <span className="ml-1">
                                {selectedCase.status === 'approved' && 'Đã duyệt'}
                                {selectedCase.status === 'rejected' && 'Đã từ chối'}
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Thông tin người duyệt */}
                  {selectedCase.accountant_name && (
                    <div className="group relative bg-gradient-to-br from-blue-50/80 to-indigo-50/80 backdrop-blur-sm rounded-2xl p-6 border border-blue-200/50 hover:border-blue-300/50 transition-all duration-300">
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-100/20 to-indigo-100/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="relative z-10">
                        <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                          <div className="relative">
                            <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-teal-400 rounded-lg blur-sm opacity-50"></div>
                            <div className="relative p-2 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-lg">
                              <CheckCircle className="w-4 h-4 text-white" />
                            </div>
                          </div>
                          <span className="ml-3">Thông tin người duyệt</span>
                        </h4>
                        <div className="space-y-4">
                          <div>
                            <label className="text-sm font-medium text-gray-500">Tên Accountant</label>
                            <p className="text-sm font-bold text-gray-900">{selectedCase.accountant_name}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">Email</label>
                            <p className="text-sm text-gray-900">{selectedCase.accountant_email || 'N/A'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">Hành động</label>
                            <p className="text-sm font-bold text-gray-900">
                              {selectedCase.status === 'approved' ? 'Đã duyệt' : 'Đã từ chối'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Notes Section */}
                {selectedCase.note && (
                  <div className="group relative bg-gradient-to-br from-yellow-50/80 to-orange-50/80 backdrop-blur-sm rounded-2xl p-6 border border-yellow-200/50 hover:border-yellow-300/50 transition-all duration-300">
                    <div className="absolute inset-0 bg-gradient-to-br from-yellow-100/20 to-orange-100/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="relative z-10">
                      <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                        <div className="relative">
                          <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-lg blur-sm opacity-50"></div>
                          <div className="relative p-2 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg">
                            <FileText className="w-4 h-4 text-white" />
                          </div>
                        </div>
                        <span className="ml-3">Lý do {selectedCase.status === 'approved' ? 'duyệt' : 'từ chối'}</span>
                      </h4>
                      <p className="text-gray-700 text-sm leading-relaxed">{selectedCase.note}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Seller Detail Modal */}
      {showSellerDetailModal && sellerDetailData && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-3xl max-w-6xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-scale-in">
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                    <User className="w-6 h-6 text-blue-600" />
                    Thống kê chi tiết: {sellerDetailData.name}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Xem thống kê và danh sách cases của seller này
                  </p>
                </div>
                <button
                  onClick={() => setShowSellerDetailModal(false)}
                  className="p-3 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-2xl transition-all duration-300 transform hover:scale-110"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Seller Popup Filters */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-4 border border-blue-100">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <CalendarDays className="w-4 h-4 inline mr-2" />
                      Thời gian
                    </label>
                    <select
                      value={sellerPopupTimeRange}
                      onChange={(e) => setSellerPopupTimeRange(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 bg-white"
                    >
                      <option value="all">Tất cả thời gian</option>
                      <option value="today">Hôm nay</option>
                      <option value="week">Tuần này</option>
                      <option value="month">Tháng này</option>
                      <option value="quarter">Quý này</option>
                      <option value="year">Năm nay</option>
                    </select>
                  </div>
                  
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <DollarSign className="w-4 h-4 inline mr-2" />
                      Tiền tệ
                    </label>
                    <select
                      value={sellerPopupCurrency}
                      onChange={(e) => setSellerPopupCurrency(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 bg-white"
                    >
                      <option value="all">Tất cả</option>
                      <option value="VND">VND</option>
                      <option value="USD">USD</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Seller Statistics Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Tổng Cases</p>
                      <p className="text-3xl font-bold text-blue-600">{sellerDetailData.total}</p>
                    </div>
                    <div className="p-3 bg-blue-100 rounded-xl">
                      <FileText className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Đã Duyệt</p>
                      <p className="text-3xl font-bold text-green-600">{sellerDetailData.approved}</p>
                    </div>
                    <div className="p-3 bg-green-100 rounded-xl">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-2xl p-6 border border-red-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Đã Từ chối</p>
                      <p className="text-3xl font-bold text-red-600">{sellerDetailData.rejected}</p>
                    </div>
                    <div className="p-3 bg-red-100 rounded-xl">
                      <XCircle className="w-6 h-6 text-red-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-2xl p-6 border border-purple-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Tổng Số tiền</p>
                      <div className="space-y-1">
                        {sellerDetailData.totalAmountVND > 0 && (
                          <p className="text-2xl font-bold text-gray-900">
                            {formatCurrency(sellerDetailData.totalAmountVND, 'VND')}
                          </p>
                        )}
                        {sellerDetailData.totalAmountUSD > 0 && (
                          <p className="text-2xl font-bold text-blue-600">
                            {formatCurrency(sellerDetailData.totalAmountUSD, 'USD')}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="p-3 bg-purple-100 rounded-xl">
                      <DollarSign className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Seller Chart */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  Biểu đồ thu chi theo ngày ({sellerPopupCurrency === 'all' ? 'Tất cả' : sellerPopupCurrency})
                </h4>
                <div className="h-80">
                  <Line 
                    data={generateSellerChartData(sellerDetailData.allCases)} 
                    options={chartOptions} 
                  />
                </div>
              </div>

              {/* Seller Cases Table */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
                <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-blue-50">
                  <h4 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <Target className="w-5 h-5 text-blue-600" />
                    Danh sách Cases của {sellerDetailData.name}
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Hiển thị {sellerDetailData.cases.length} cases (Tổng: {sellerDetailData.allCases.length})
                  </p>
                </div>

                {sellerDetailData.cases.length === 0 ? (
                  <div className="p-8 text-center">
                    <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Không có cases nào</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Thông tin Case
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Loại & Số tiền
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Trạng thái
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Thao tác
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {sellerDetailData.cases.map((case_, index) => (
                          <tr key={case_.id} className="hover:bg-blue-50/50 transition-all duration-300">
                            <td className="px-6 py-4">
                              <div>
                                <div className="text-sm font-bold text-gray-900">{case_.title}</div>
                                <div className="text-sm text-gray-600 mt-1">{case_.content || case_.description || 'Không có mô tả'}</div>
                                <div className="text-xs text-gray-500 mt-2">{formatDate(case_.created_at || case_.updated_at || case_.date)}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <span className={`inline-flex items-center px-2 py-1 text-xs font-bold rounded-full ${getTypeColor(case_.type)}`}>
                                  {case_.type === 'income' || case_.type === 'thu' ? 'Thu' : 'Chi'}
                                </span>
                                <div className="text-right">
                                  <div className="text-sm font-bold text-gray-900">
                                    {formatCurrency(case_.amount, case_.currency)}
                                  </div>
                                  <div className="text-xs text-gray-500">{case_.currency}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border-2 ${getStatusColor(case_.status)}`}>
                                {getStatusIcon(case_.status)}
                                <span className="ml-1">
                                  {case_.status === 'approved' && 'Đã duyệt'}
                                  {case_.status === 'rejected' && 'Đã từ chối'}
                                </span>
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <button
                                onClick={() => {
                                  setSelectedCase(case_);
                                  setShowDetailModal(true);
                                  // Không đóng modal seller detail, để modal case detail hiển thị trên cùng
                                }}
                                className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-lg transition-all duration-300"
                                title="Xem chi tiết"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountantStatistics;

// Custom CSS for animations
const styles = `
  @keyframes fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes fade-in-up {
    from { 
      opacity: 0; 
      transform: translateY(30px); 
    }
    to { 
      opacity: 1; 
      transform: translateY(0); 
    }
  }
  
  @keyframes scale-in {
    from { 
      opacity: 0; 
      transform: scale(0.95); 
    }
    to { 
      opacity: 1; 
      transform: scale(1); 
    }
  }
  
  @keyframes slide-in-right {
    from { 
      opacity: 0; 
      transform: translateX(30px); 
    }
    to { 
      opacity: 1; 
      transform: translateX(0); 
    }
  }
  
  @keyframes bounce-in {
    0% {
      opacity: 0;
      transform: scale(0.3);
    }
    50% {
      opacity: 1;
      transform: scale(1.05);
    }
    70% {
      transform: scale(0.9);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  @keyframes shimmer {
    0% {
      background-position: -200px 0;
    }
    100% {
      background-position: calc(200px + 100%) 0;
    }
  }
  
  @keyframes pulse-glow {
    0%, 100% {
      box-shadow: 0 0 5px rgba(59, 130, 246, 0.5);
    }
    50% {
      box-shadow: 0 0 20px rgba(59, 130, 246, 0.8), 0 0 30px rgba(59, 130, 246, 0.6);
    }
  }
  
  .animate-fade-in {
    animation: fade-in 0.6s ease-out;
  }
  
  .animate-fade-in-up {
    animation: fade-in-up 0.8s ease-out forwards;
    opacity: 0;
  }
  
  .animate-scale-in {
    animation: scale-in 0.5s ease-out;
  }
  
  .animate-slide-in-right {
    animation: slide-in-right 0.6s ease-out;
  }
  
  .animate-bounce-in {
    animation: bounce-in 0.8s ease-out;
  }
  
  .animate-shimmer {
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    background-size: 200px 100%;
    animation: shimmer 2s infinite;
  }
  
  .animate-pulse-glow {
    animation: pulse-glow 2s ease-in-out infinite;
  }
  
  /* Smooth scrolling */
  html {
    scroll-behavior: smooth;
  }
  
  /* Custom scrollbar */
  ::-webkit-scrollbar {
    width: 10px;
  }
  
  ::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 8px;
  }
  
  ::-webkit-scrollbar-thumb {
    background: linear-gradient(to bottom, #3b82f6, #6366f1, #8b5cf6);
    border-radius: 8px;
    border: 2px solid rgba(255, 255, 255, 0.1);
  }
  
  ::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(to bottom, #2563eb, #4f46e5, #7c3aed);
  }
  
  /* Enhanced hover effects */
  .hover-lift {
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .hover-lift:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  }
  
  /* Professional gradient backgrounds */
  .gradient-bg {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  }
  
  .gradient-bg-alt {
    background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  }
  
  .gradient-bg-success {
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  }
  
  /* Enhanced glassmorphism effect */
  .glass {
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.37);
  }
  
  .glass-dark {
    background: rgba(0, 0, 0, 0.15);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  /* Professional shadows */
  .shadow-professional {
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }
  
  .shadow-professional-hover {
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }
  
  /* Text gradients */
  .text-gradient {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  .text-gradient-alt {
    background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  /* Loading animations */
  .loading-dots {
    display: inline-block;
  }
  
  .loading-dots::after {
    content: '';
    animation: dots 1.5s steps(5, end) infinite;
  }
  
  @keyframes dots {
    0%, 20% {
      content: '';
    }
    40% {
      content: '.';
    }
    60% {
      content: '..';
    }
    80%, 100% {
      content: '...';
    }
  }
  
  /* Professional focus states */
  .focus-ring {
    transition: all 0.2s ease-in-out;
  }
  
  .focus-ring:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.5);
    transform: scale(1.02);
  }
  
  /* Smooth transitions for all interactive elements */
  * {
    transition: all 0.2s ease-in-out;
  }
  
  /* Professional button styles */
  .btn-primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    color: white;
    padding: 12px 24px;
    border-radius: 12px;
    font-weight: 600;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  }
  
  .btn-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.6);
  }
  
  .btn-secondary {
    background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
    border: none;
    color: white;
    padding: 12px 24px;
    border-radius: 12px;
    font-weight: 600;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(240, 147, 251, 0.4);
  }
  
  .btn-secondary:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(240, 147, 251, 0.6);
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
} 